import cx from "classnames";
import React, { type FC, useCallback, useRef, useState } from "react";
import { useEffectOnce } from "react-use";

import type { PageMetadata } from "@moment/api-collab/api-types";

import { TextboxAuto } from "~/components/common/inputs/TextboxAuto";
import { usePageActor } from "~/state";
import { DEFAULT_PAGE_TITLE } from "~/strings";

import { useEasel } from "../easel";
import { useCurrentDocument } from "../hooks/useCurrentDocument";
import { cursorOffsets } from "./cursor";

export type PageTitleProps = {
	page: PageMetadata;
	writable: boolean;
};

export const PageTitle: FC<PageTitleProps> = ({ page, writable }) => {
	// Hooks
	const { documentId } = useEasel();
	const currentDocument = useCurrentDocument();
	const pageActor = usePageActor(documentId, page.id);

	// Constants
	const isUntitled = page?.title === "";
	const originalTitle = page?.title;

	// State
	const [editingTitle, setEditingTitle] = useState(originalTitle);
	const [isRenaming, setIsRenaming] = useState(false);
	const title = isRenaming ? editingTitle : originalTitle;

	// Refs
	const textbox = useRef<HTMLTextAreaElement>(null);

	// Functions
	const focusEditor = useCallback(() => {
		const editorDom = document.querySelector<HTMLElement>(".ProseMirror[contenteditable]");
		editorDom?.focus();
	}, []);

	const saveTitle = async () => {
		// Only save the title if it changed
		if (currentDocument?.id && title !== originalTitle) {
			pageActor?.send({ type: "page.nameChanged", payload: { newName: title } });
		}
	};

	const handleOnBlur = () => {
		setIsRenaming(false);
		void saveTitle();
	};

	const handleOnFocus = () => {
		setEditingTitle(originalTitle);
		setIsRenaming(true);
	};

	const revertTitle = () => {
		setEditingTitle(originalTitle);
	};

	const handleOnKeyDown: React.KeyboardEventHandler = (event) => {
		const key = event.key;

		switch (key) {
			case "Enter": {
				event.preventDefault();
				focusEditor();
				return;
			}
			case "ArrowDown": {
				const title = textbox.current;

				if (title === null) {
					return;
				}

				/**
				 * check if cursor inside text area is on the last line
				 * if it is, focus on the first cell in the editor
				 */
				const { lineHeight, offsetTop } = cursorOffsets(title, title.selectionStart);
				if (title.offsetHeight - offsetTop - lineHeight < lineHeight) {
					event.preventDefault();
					event.stopPropagation();
					focusEditor();
					return;
				}
				break;
			}
			case "Esc":
			case "Escape":
				revertTitle();
				break;
		}
	};

	const textboxOnChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
		setEditingTitle(event.currentTarget.value);
	};

	/**
	 * Focus the textbox when mounted if untitled
	 */
	useEffectOnce(() => {
		if (isUntitled) {
			// ProseMirror gets first dibs on focus so we have to use a timeout to grab it from ProseMirror
			setTimeout(() => {
				textbox.current?.focus();
			}, 0);
		}
	});

	const className = cx(
		"resize-none",
		"border-transparent",
		"bg-transparent",
		"p-0",
		"font-bold",
		"placeholder:text-tertiary-solid",
		"placeholder:opacity-50",
		"focus:outline-none",
		"pl-2",
		"-ml-2",
		"text-3xl",
		"@lg/main:text-[32px] @md/main:leading-[32px]",
		"@xl/main:text-[40px] @lg/main:leading-[40px]"
	);

	return writable ? (
		<TextboxAuto
			disabled={!writable}
			ref={textbox}
			className={className}
			placeholder={DEFAULT_PAGE_TITLE}
			value={title}
			spellCheck={false}
			onKeyDown={handleOnKeyDown}
			onChange={textboxOnChange}
			onBlur={handleOnBlur}
			onFocus={handleOnFocus}
			maxLength={/* max length of title */ 400}
			// The autoFocus prop is intentionally omitted for a11y - https://github.com/jsx-eslint/eslint-plugin-jsx-a11y/blob/main/docs/rules/no-autofocus.md
		/>
	) : (
		<h1
			className={cx(
				className,
				// IMPORTANT: pb-1 to avoid cutting off the bottoms of lowercase y, j, g, p, q, and
				// other letters that have a stem.
				"pb-1",
				"!my-0" // This has to be important because of the rich-text classes
			)}
		>
			{title}
		</h1>
	);
};
